/**
 * Visualizer Registry - Dynamic system for registering and managing visualizers
 */

import { IVisualizer, ViewMode, VisualizerMetadata } from './types';

export class VisualizerRegistry {
  private static instance: VisualizerRegistry;
  private visualizers: Map<ViewMode, IVisualizer> = new Map();
  
  private constructor() {}
  
  /**
   * Get the singleton instance
   */
  public static getInstance(): VisualizerRegistry {
    if (!VisualizerRegistry.instance) {
      VisualizerRegistry.instance = new VisualizerRegistry();
    }
    return VisualizerRegistry.instance;
  }
  
  /**
   * Register a new visualizer
   */
  public register(visualizer: IVisualizer): void {
    if (this.visualizers.has(visualizer.metadata.id)) {
      console.warn(`Visualizer with id '${visualizer.metadata.id}' is already registered. Overwriting.`);
    }
    this.visualizers.set(visualizer.metadata.id, visualizer);
  }
  
  /**
   * Unregister a visualizer
   */
  public unregister(id: ViewMode): boolean {
    return this.visualizers.delete(id);
  }
  
  /**
   * Get a visualizer by ID
   */
  public get(id: ViewMode): IVisualizer | undefined {
    return this.visualizers.get(id);
  }
  
  /**
   * Get all registered visualizers
   */
  public getAll(): IVisualizer[] {
    return Array.from(this.visualizers.values());
  }
  
  /**
   * Get all visualizer metadata
   */
  public getAllMetadata(): VisualizerMetadata[] {
    return this.getAll().map(v => v.metadata);
  }
  
  /**
   * Get visualizers by category
   */
  public getByCategory(category: VisualizerMetadata['category']): IVisualizer[] {
    return this.getAll().filter(v => v.metadata.category === category);
  }
  
  /**
   * Check if a visualizer is registered
   */
  public has(id: ViewMode): boolean {
    return this.visualizers.has(id);
  }
  
  /**
   * Clear all visualizers
   */
  public clear(): void {
    this.visualizers.clear();
  }
  
  /**
   * Get the number of registered visualizers
   */
  public get count(): number {
    return this.visualizers.size;
  }
}

/**
 * Convenience function to get the registry instance
 */
export const getVisualizerRegistry = () => VisualizerRegistry.getInstance();
